import React from "react";
import { useState, useEffect, useRef } from "react";
import { motion, useInView } from "framer-motion";
import Title from "../layouts/Title";
import JSImg from "../../assets/images/js.png";
import TSTransparentImg from "../../assets/images/ts-transparent_opt.png";
import ReactImg from "../../assets/images/React.webp";
import NextImg from "../../assets/images/next.webp";
import nodeImg from "../../assets/images/node_opt.png";
import nativeImg from "../../assets/images/native_opt.png";
import mongoImg from "../../assets/images/mongo_opt.png";
import expressImg from "../../assets/images/express_opt.png";
import htmlImg from "../../assets/images/html_opt.png";
import cssImg from "../../assets/images/css_opt.png";
import gitImg from "../../assets/images/git_opt.png";
import jestImg from "../../assets/images/jest_opt.png";
import serverlessImg from "../../assets/images/serverless_opt.png";
import dockerImg from "../../assets/images/docker.png";
import reduxImg from "../../assets/images/redux_opt.png";
import elastickImg from "../../assets/images/elastic_opt.png";
import antdImg from "../../assets/images/antd_opt.png";
import awsImg from "../../assets/images/aws_opt.png";
import jiraImg from "../../assets/images/jira_opt.png";
import webpackImg from "../../assets/images/webpack_opt.png";
import confluenceImg from "../../assets/images/confluence_opt.png";
import npmImg from "../../assets/images/npm_opt.png";
import postmanImg from "../../assets/images/postman_opt.png";
import firebaseImg from "../../assets/images/firebase_opt.png";
import jenkinsImg from "../../assets/images/jenkins_opt.png";
import muiImg from "../../assets/images/mui_opt.png";
import navitaireImg from "../../assets/images/navitaire_opt.png";
import netlifyImg from "../../assets/images/netlify_opt.png";
import azureImg from "../../assets/images/Microsoft_Azure.svg.png";
import AnimatedTechGraphic from "./AnimatedTechGraphic";

const logos = [
  { img: JSImg, name: "JavaScript", color: "#EFDB50" },
  { img: TSTransparentImg, name: "TypeScript", color: "#007ACC" },
  { img: ReactImg, name: "React", color: "#61DAFB" },
  { img: NextImg, name: "Next.js", color: "#ECECEC" },
  { img: nativeImg, name: "React Native", color: "#61DAFB" },
  { img: nodeImg, name: "Node.js", color: "#40873F" },
  { img: mongoImg, name: "MongoDB", color: "#94795D" },
  { img: expressImg, name: "Express", color: "#828282" },
  { img: htmlImg, name: "HTML", color: "#E44D27" },
  { img: cssImg, name: "CSS", color: "#1B73BA" },
  { img: gitImg, name: "Git", color: "#F05133" },
  { img: dockerImg, name: "Docker", color: "#016DB8" },
  { img: serverlessImg, name: "Serverless", color: "#F15954" },
  { img: reduxImg, name: "Redux", color: "#764ABC" },
  { img: elastickImg, name: "Elasticsearch", color: "#FEC514" },
  { img: antdImg, name: "Ant Design", color: "#F1736F" },
  { img: jiraImg, name: "Jira", color: "#2484FF" },
  { img: awsImg, name: "AWS", color: "#FF9901" },
  { img: webpackImg, name: "Webpack", color: "#8ED6FB" },
  { img: confluenceImg, name: "Confluence", color: "#2684FF" },
  { img: npmImg, name: "npm", color: "#D50C00" },
  { img: postmanImg, name: "Postman", color: "#FD6C35" },
  { img: firebaseImg, name: "Firebase", color: "#FFCB2C" },
  { img: jenkinsImg, name: "Jenkins", color: "#D43833" },
  { img: jestImg, name: "Jest", color: "#CC4714" },
  { img: muiImg, name: "Material UI", color: "#027FFE" },
  { img: navitaireImg, name: "Navitaire", color: "#027FFE" },
  { img: netlifyImg, name: "Netlify", color: "#04B2AE" },
  { img: azureImg, name: "Microsoft Azure", color: "#0078D4" },
];

const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < breakpoint);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < breakpoint);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [breakpoint]);

  return isMobile;
};

// Toggle: 'bento' | 'centered' | 'strip'
const ABOUT_SECTION_DESIGN = "strip";

const aboutCopy = {
  intro:
    "I am a passionate Senior Software Developer with 5+ years of experience building scalable, high-performance web and mobile applications. I focus on writing clean, maintainable, and well-tested code to deliver reliable and efficient solutions with high quality standards.",
  outro:
    "Experienced in agile teams, I enjoy mentoring developers, optimizing system architecture, and solving complex problems within the JavaScript ecosystem. I’m passionate about building scalable products, improving performance, and creating seamless user experiences.",
};

const TechStackGrid = ({ logos: logosList }) => {
  const [hoveredTech, setHoveredTech] = useState(null);
  const gridRef = useRef(null);
  // Single observer for the entire grid — replaces 28 individual whileInView observers
  const isInView = useInView(gridRef, { once: true, margin: "-60px" });

  return (
    <div
      ref={gridRef}
      className="flex-1 grid grid-cols-4 sml:grid-cols-5 md:grid-cols-6 xl:grid-cols-8 gap-2 sml:gap-4"
    >
      {logosList.map((logo, index) => {
        const hoverColor = logo?.color ?? "rgba(255,255,255,0.4)";
        const glowShadow = `0 0 0 2px ${hoverColor}60, 0 16px 40px rgba(0,0,0,0.35), 0 0 40px ${hoverColor}35, 0 0 60px ${hoverColor}15`;
        return (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 12 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 }}
            whileHover={{ y: -6, scale: 1.03, transition: { duration: 0.2, ease: "easeOut" } }}
            transition={{ duration: 0.35, delay: Math.min(index * 0.02, 0.25), ease: [0.25, 1, 0.5, 1] }}
            className="group cursor-pointer"
          >
            <div
              className="relative aspect-square flex flex-col items-center justify-center rounded-2xl border border-white/15 bg-gradient-to-br from-white/[0.09] via-white/[0.04] to-white/[0.02] p-2 sm:p-4 transition-all duration-300 overflow-hidden"
              onMouseEnter={(e) => {
                setHoveredTech(index);
                e.currentTarget.style.borderColor = hoverColor;
                e.currentTarget.style.boxShadow = `${glowShadow}, inset 0 1px 0 rgba(255,255,255,0.08)`;
                e.currentTarget.style.background = `linear-gradient(135deg, ${hoverColor}22 0%, rgba(255,255,255,0.08) 50%, ${hoverColor}12 100%)`;
              }}
              onMouseLeave={(e) => {
                setHoveredTech(null);
                e.currentTarget.style.borderColor = "";
                e.currentTarget.style.boxShadow = "";
                e.currentTarget.style.background = "";
              }}
            >
              {hoveredTech === index && (
                <div
                  key={`shimmer-${index}-${hoveredTech}`}
                  className="absolute inset-0 w-1/2 pointer-events-none rounded-xl"
                  style={{
                    background: `linear-gradient(105deg, transparent 0%, ${hoverColor}30 40%, rgba(255,255,255,0.4) 50%, ${hoverColor}30 60%, transparent 100%)`,
                    animation: "tech-card-shimmer 0.55s ease-out forwards",
                  }}
                />
              )}
              <div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none rounded-xl"
                style={{ background: `radial-gradient(circle at 50% 50%, ${hoverColor}28 0%, ${hoverColor}12 40%, transparent 70%)` }}
              />
              <img
                src={logo?.img}
                alt={logo?.name || "technology logo"}
                className="relative w-10 h-10 sm:w-11 sm:h-11 object-contain transition-all duration-200 ease-out group-hover:scale-110 drop-shadow-sm group-hover:drop-shadow-md"
              />
              <span className="absolute bottom-1.5 left-1.5 right-1.5 rounded-md border border-white/10 bg-black/25 px-1.5 py-0.5 text-[9px] sm:text-[10px] text-gray-200 text-center leading-tight tracking-wide backdrop-blur-sm opacity-90 sm:opacity-0 sm:translate-y-1 sm:group-hover:opacity-100 sm:group-hover:translate-y-0 transition-all duration-200">
                {logo?.name}
              </span>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};

const Features = () => {
  const isMobile = useIsMobile();

  const AboutBento = () => (
    <div
      id="features"
      className="relative flex flex-col lgl:flex-row gap-6 lgl:gap-8 items-stretch lgl:items-center"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.92 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.6, ease: [0.25, 1, 0.5, 1] }}
        className="relative shrink-0 flex justify-center lgl:order-2"
      >
        <div className="relative">
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-designColor/40 via-purple-500/30 to-cyan-400/20 blur-2xl scale-110" />
          <div className="relative p-1.5 rounded-full bg-gradient-to-br from-designColor via-pink-500 to-purple-600 shadow-[0_0_40px_rgba(255,1,79,0.25)]">
            <div className="relative rounded-full overflow-hidden border-2 border-white/10 bg-white/5 backdrop-blur-sm">
              <img
                src="/profile_optimized.jpg"
                alt="About me"
                className="w-[220px] h-[220px] sm:w-[260px] sm:h-[260px] lgl:w-[280px] lgl:h-[280px] object-cover"
              />
            </div>
          </div>
        </div>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.5, delay: 0.15, ease: [0.25, 1, 0.5, 1] }}
        className="relative flex-1 min-w-0 rounded-2xl border border-white/10 bg-white/[0.06] backdrop-blur-2xl shadow-[0_25px_80px_rgba(0,0,0,0.45)] overflow-hidden"
      >
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-designColor via-pink-500 to-purple-600" />
        <div className="pointer-events-none absolute top-0 right-0 w-64 h-64 rounded-full bg-[radial-gradient(circle,#6366f115,transparent_70%)] blur-2xl" />
        <div className="pointer-events-none absolute bottom-0 left-1/4 w-48 h-48 rounded-full bg-[radial-gradient(circle,#ff014f10,transparent_70%)] blur-2xl" />
        <div className="relative pl-6 sm:pl-8 pr-6 sm:pr-8 py-6 sm:py-8">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3.5 py-1.5 text-[10px] font-medium uppercase tracking-[0.2em] text-gray-400 backdrop-blur-md mb-5">
            <span className="h-1.5 w-1.5 rounded-full bg-designColor" />
            Features · About Me
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight font-titleFont mb-2">
            <span className="bg-gradient-to-r from-white via-gray-100 to-gray-300 bg-clip-text text-transparent drop-shadow-sm">
              Senior Software Developer
            </span>
          </h2>
          <div className="flex flex-wrap items-center gap-2 mb-6">
            <span className="inline-flex items-center rounded-full bg-designColor/10 border border-designColor/20 px-3 py-1 text-xs font-medium text-designColor">
              5+ years
            </span>
            <span className="text-gray-500 text-sm">·</span>
            <span className="text-gray-400 text-sm font-medium">Web & mobile</span>
            <span className="text-gray-500 text-sm">·</span>
            <span className="text-gray-400 text-sm font-medium">Clean code & scale</span>
          </div>
          <div className="space-y-5">
            <p className="text-gray-300/95 text-[15px] sm:text-base leading-[1.75] tracking-wide">
              {aboutCopy.intro}
            </p>
            <p className="text-gray-300/95 text-[15px] sm:text-base leading-[1.75] tracking-wide">
              {aboutCopy.outro}
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );

  const AboutCentered = () => (
    <div
      id="features"
      className="relative flex justify-center"
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.5, ease: [0.25, 1, 0.5, 1] }}
        className="relative w-full max-w-3xl rounded-[1.75rem] border border-white/10 bg-gradient-to-b from-white/[0.08] to-white/[0.02] backdrop-blur-2xl shadow-[0_32px_64px_rgba(0,0,0,0.5)] overflow-hidden"
      >
        <div className="absolute inset-0 rounded-[1.75rem] bg-[linear-gradient(135deg,transparent_0%,rgba(255,1,79,0.03)_50%,transparent_100%)]" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 h-px w-3/4 bg-gradient-to-r from-transparent via-designColor/50 to-transparent" />
        <div className="relative px-6 sm:px-10 pt-8 sm:pt-10 pb-8 sm:pb-10 flex flex-col items-center text-center">
          <div className="relative mb-6">
            <div className="absolute -inset-1 rounded-2xl bg-gradient-to-br from-designColor/40 via-purple-500/25 to-cyan-400/20 blur-xl" />
            <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-white/5 p-1.5 shadow-[0_20px_50px_rgba(0,0,0,0.4)]">
              <img
                src="/profile_optimized.jpg"
                alt="About me"
                className="w-[200px] h-[200px] sm:w-[220px] sm:h-[220px] object-cover rounded-xl"
              />
            </div>
          </div>
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3.5 py-1.5 text-[10px] font-medium uppercase tracking-[0.2em] text-gray-400 mb-4">
            <span className="h-1.5 w-1.5 rounded-full bg-designColor" />
            About Me
          </div>
          <h2 className="text-xl sm:text-2xl font-bold tracking-tight font-titleFont mb-2">
            <span className="bg-gradient-to-r from-white via-gray-100 to-gray-300 bg-clip-text text-transparent drop-shadow-sm">
              Senior Software Developer
            </span>
          </h2>
          <div className="flex flex-wrap items-center justify-center gap-2 mb-6">
            <span className="inline-flex items-center rounded-full bg-designColor/10 border border-designColor/20 px-3 py-1 text-xs font-medium text-designColor">
              5+ years
            </span>
            <span className="text-gray-500 text-sm">·</span>
            <span className="text-gray-400 text-sm font-medium">Web & mobile</span>
            <span className="text-gray-500 text-sm">·</span>
            <span className="text-gray-400 text-sm font-medium">Clean code & scale</span>
          </div>
          <div className="w-12 h-0.5 rounded-full bg-gradient-to-r from-designColor to-purple-500 mb-6" />
          <div className="space-y-5 text-left max-w-2xl">
            <p className="text-gray-300/95 text-[15px] sm:text-base leading-[1.75] tracking-wide">
              {aboutCopy.intro}
            </p>
            <p className="text-gray-300/95 text-[15px] sm:text-base leading-[1.75] tracking-wide">
              {aboutCopy.outro}
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );

  const AboutStrip = () => (
    <div id="features" className="relative overflow-hidden">

      {/* ── MOBILE layout (hidden on mdl+) ── */}
      <div className="mdl:hidden flex flex-col p-5">
        {/* Top row: avatar + identity */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-20px" }}
          transition={{ duration: 0.5, ease: [0.25, 1, 0.5, 1] }}
          className="flex items-center gap-4 mb-6"
        >
          {/* Avatar */}
          <div className="relative shrink-0">
            <div className="absolute -inset-1 rounded-full bg-gradient-to-br from-designColor/50 via-purple-500/30 to-cyan-400/20 blur-lg" />
            <div className="relative z-10 rounded-full overflow-hidden ring-2 ring-white/20 p-0.5 bg-gradient-to-br from-white/20 to-white/5">
              <img
                src="/profile_optimized.jpg"
                alt="About me"
                className="w-[100px] h-[100px] object-cover rounded-full"
              />
            </div>
          </div>
          {/* Identity */}
          <div className="flex flex-col gap-2 min-w-0">
            <div className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[9px] font-medium uppercase tracking-[0.18em] text-gray-400 w-fit">
              <span className="h-1.5 w-1.5 rounded-full bg-designColor shrink-0" />
              About Me
            </div>
            <h2 className="text-[18px] font-bold tracking-tight font-titleFont leading-tight">
              <span className="bg-gradient-to-r from-white via-gray-100 to-gray-300 bg-clip-text text-transparent">
                Senior Software Developer
              </span>
            </h2>
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="inline-flex items-center rounded-full bg-designColor/10 border border-designColor/20 px-2.5 py-0.5 text-[11px] font-medium text-designColor">
                5+ years
              </span>
              <span className="text-gray-500 text-xs">·</span>
              <span className="text-gray-400 text-xs font-medium">Web & Mobile</span>
            </div>
          </div>
        </motion.div>

        {/* Divider */}
        <div className="h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent mb-5" />

        {/* Description */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-20px" }}
          transition={{ duration: 0.5, delay: 0.15, ease: [0.25, 1, 0.5, 1] }}
          className="space-y-4"
        >
          <p className="text-gray-300/90 text-sm leading-[1.8] tracking-wide">{aboutCopy.intro}</p>
          <p className="text-gray-300/90 text-sm leading-[1.8] tracking-wide">{aboutCopy.outro}</p>
        </motion.div>
      </div>

      {/* ── DESKTOP layout (hidden below mdl) ── */}
      <div className="hidden mdl:flex mdl:flex-row mdl:items-stretch gap-0">
        <motion.div
          initial={{ opacity: 0, x: -16 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.5, ease: [0.25, 1, 0.5, 1] }}
          className="relative shrink-0 flex items-center justify-end py-12 pl-12 pr-8"
        >
          <div className="relative">
            <div className="absolute -inset-1 rounded-full bg-gradient-to-br from-designColor/50 via-purple-500/30 to-cyan-400/20 blur-xl" />
            <div className="relative z-10 rounded-full overflow-hidden ring-2 ring-white/20 ring-offset-2 ring-offset-white/5 p-1 bg-gradient-to-br from-white/20 to-white/5">
              <img
                src="/profile_optimized.jpg"
                alt="About me"
                className="w-[220px] h-[220px] object-cover rounded-full"
              />
            </div>
          </div>
        </motion.div>
        <div className="w-px self-stretch bg-gradient-to-b from-transparent via-white/10 to-transparent shrink-0" />
        <motion.div
          initial={{ opacity: 0, x: 16 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.5, delay: 0.1, ease: [0.25, 1, 0.5, 1] }}
          className="relative flex-1 min-w-0 py-12 pr-12 pl-12"
        >
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3.5 py-1.5 text-[10px] font-medium uppercase tracking-[0.2em] text-gray-400 mb-5">
            <span className="h-1.5 w-1.5 rounded-full bg-designColor" />
            About Me
          </div>
          <h2 className="text-3xl lgl:text-4xl font-bold tracking-tight font-titleFont mb-2">
            <span className="bg-gradient-to-r from-white via-gray-100 to-gray-300 bg-clip-text text-transparent drop-shadow-sm">
              Senior Software Developer
            </span>
          </h2>
          <div className="flex flex-wrap items-center gap-2 mb-6">
            <span className="inline-flex items-center rounded-full bg-designColor/10 border border-designColor/20 px-3 py-1 text-xs font-medium text-designColor">5+ years</span>
            <span className="text-gray-500 text-sm">·</span>
            <span className="text-gray-400 text-sm font-medium">Web & Mobile</span>
            <span className="text-gray-500 text-sm">·</span>
            <span className="text-gray-400 text-sm font-medium">Manage Teams</span>
          </div>
          <div className="space-y-5">
            <p className="text-gray-300/95 text-base leading-[1.75] tracking-wide">{aboutCopy.intro}</p>
            <p className="text-gray-300/95 text-base leading-[1.75] tracking-wide">{aboutCopy.outro}</p>
          </div>
        </motion.div>
      </div>
    </div>
  );

  const aboutSection =
    ABOUT_SECTION_DESIGN === "strip" ? <AboutStrip /> :
      ABOUT_SECTION_DESIGN === "centered" ? <AboutCentered /> :
        <AboutBento />;

  return (
    <div className="pb-10">
      <div className="mx-4 md:mx-6 lg:mx-8 rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.06] via-white/[0.03] to-white/[0.02] backdrop-blur-sm shadow-[0_20px_60px_rgba(0,0,0,0.3)] overflow-hidden">
        {aboutSection}
      </div>
      <div id="technologies" className="py-10 scroll-mt-24">
        <div className="mb-8">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-[10px] font-medium uppercase tracking-[0.2em] text-gray-500">
            <span className="h-1.5 w-1.5 rounded-full bg-designColor" />
            Tech stack
          </span>
          <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight mt-3">Technologies & tools</h2>
          <span className="mt-2 block h-px w-12 bg-gradient-to-r from-designColor/50 to-transparent rounded-full" />
        </div>

        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8 lg:gap-10">
          <motion.div
            initial={{ opacity: 0, x: -12 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.5, ease: [0.25, 1, 0.5, 1] }}
            className="hidden lg:flex flex-shrink-0 w-full max-w-[280px] sm:max-w-[320px] mx-auto lg:mx-0 items-center justify-center p-4 lg:p-0"
          >
            <AnimatedTechGraphic />
          </motion.div>

          <TechStackGrid logos={logos} />
        </div>
      </div>
    </div>
  );
};

export default Features;
